'use client';

import { useTranslations } from 'next-intl';
import { motion } from 'framer-motion';
import { Target, Users, LayoutDashboard } from 'lucide-react';

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: [0.25, 0.1, 0.25, 1] as const } },
};

export function WhyGrid() {
  const t = useTranslations('landing.whyGrid');

  const features = [
    {
      icon: Target,
      title: t('feature1Title'),
      description: t('feature1Desc'),
      mockup: (
        <div className="mt-6 flex flex-col gap-2 rounded-xl bg-black/40 border border-white/5 p-3">
          <div className="flex items-center gap-2 mb-2">
            <div className="w-6 h-6 rounded-full bg-primary/20 flex items-center justify-center">
              <div className="w-2 h-2 rounded-full bg-primary" />
            </div>
            <div className="h-2 w-24 bg-white/20 rounded-full" />
          </div>
          <div className="h-1.5 w-full bg-white/10 rounded-full overflow-hidden">
            <div className="h-full bg-primary/80 w-[85%]" />
          </div>
          <div className="flex justify-between mt-1">
            <div className="h-1.5 w-12 bg-white/10 rounded-full" />
            <div className="h-1.5 w-8 bg-primary/50 rounded-full" />
          </div>
        </div>
      ),
    },
    {
      icon: Users,
      title: t('feature2Title'),
      description: t('feature2Desc'),
      mockup: (
        <div className="mt-6 flex flex-col gap-3 rounded-xl bg-black/40 border border-white/5 p-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-6 h-6 rounded bg-white/5 border border-white/10" />
              <div className="flex-1">
                <div className="h-1.5 w-full bg-white/10 rounded-full mb-1.5" />
                <div className="h-1.5 w-2/3 bg-white/5 rounded-full" />
              </div>
              <div className="w-8 h-4 rounded-full bg-primary/20 flex items-center justify-center">
                <div className="w-4 h-1 bg-primary/60 rounded-full" />
              </div>
            </div>
          ))}
        </div>
      ),
    },
    {
      icon: LayoutDashboard,
      title: t('feature3Title'),
      description: t('feature3Desc'),
      mockup: (
        <div className="mt-6 grid grid-cols-2 gap-2 rounded-xl bg-black/40 border border-white/5 p-3">
          <div className="col-span-2 h-16 rounded bg-white/5 border border-white/10 flex items-end p-2">
            <div className="w-full flex items-end justify-between gap-1 h-8">
              {[40, 70, 45, 90, 65, 80].map((h, i) => (
                <div
                  key={i}
                  className="w-full bg-primary/40 rounded-t-sm"
                  style={{ height: `${h}%` }}
                />
              ))}
            </div>
          </div>
          <div className="h-10 rounded bg-white/5 border border-white/10" />
          <div className="h-10 rounded bg-white/5 border border-white/10" />
        </div>
      ),
    },
  ];

  return (
    <section
      data-theme="dark"
      className="relative py-[120px] overflow-hidden bg-black rounded-t-[2rem] -mt-8 z-10"
    >
      <div className="container max-w-[1440px] mx-auto px-4 relative z-10">
        <div className="max-w-3xl mx-auto text-center mb-20">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.4 }}
            className="text-xs font-semibold uppercase tracking-widest text-primary mb-4"
          >
            {t('badge')}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5, delay: 0.06 }}
            className="text-3xl md:text-5xl font-serif text-white mb-6"
          >
            {t('headline')}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5, delay: 0.12 }}
            className="text-lg text-white/60"
          >
            {t('subheadline')}
          </motion.p>
        </div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: '-100px' }}
          className="grid md:grid-cols-3 gap-8"
        >
          {features.map((feature, idx) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={idx}
                variants={itemVariants}
                className="group relative flex flex-col justify-between p-10 rounded-3xl bg-white/[0.02] border border-white/10 hover:bg-white/[0.04] hover:border-white/20 transition-all duration-300 backdrop-blur-md overflow-hidden"
              >
                {/* Subtle top glow on hover */}
                <div className="absolute top-0 left-1/2 -translate-x-1/2 w-3/4 h-1 bg-gradient-to-r from-transparent via-primary/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 blur-sm" />

                <div>
                  <div className="w-12 h-12 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center mb-6 text-primary group-hover:scale-110 transition-transform duration-300">
                    <Icon strokeWidth={1.5} className="w-6 h-6" />
                  </div>

                  <h3 className="text-xl font-medium text-white mb-3 tracking-tight">
                    {feature.title}
                  </h3>

                  <p className="text-sm text-white/75 leading-relaxed">{feature.description}</p>
                </div>

                {/* CSS Mockup Representation */}
                {feature.mockup}
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
}
